import {
  Button,
  Card,
  Flex,
  Input,
  Space,
  Spin,
  Switch,
  Table,
  TableColumnsType,
  Typography
} from "antd";
import { changePrimaryLocationForAccount } from "api/services/AccountService";
import { getAllLocations } from "api/services/LocationService";
import { TAccount, TLocation } from "api/services/models/types";
import { WebDPrivilege } from "api/services/models/WebDUser";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { ReactComponent as AddLocation } from "Icons/vuesax/building.svg";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { ReactComponent as PrimaryLocation } from "Icons/vuesax/tick-circle.svg";
import { useCallback, useEffect, useState } from "react";
import AddOrUpdateLocation from "./AddOrUpdateLocation";

type TModalLocationState = {
  location?: TLocation;
  showModal: boolean;
};

const PrimaryLocatonCell = (
  location: TLocation,
  account: TAccount,
  onChangePrimaryLocation: (location: TLocation) => void,
  hasManageAccountPrivilege: boolean
) => {
  return (
    <Flex justify="center" align="center" className="primary-location">
      {location.locationId !== account.primaryLocationId && (
        <Switch
          defaultChecked
          value={location.locationId === account.primaryLocationId}
          onChange={() => onChangePrimaryLocation(location)}
          disabled={!hasManageAccountPrivilege}
        />
      )}
      {location.locationId === account.primaryLocationId && <PrimaryLocation />}
    </Flex>
  );
};

const composeColumns = (
  account: TAccount,
  onChangePrimaryLocation: (location: TLocation) => void,
  onEditLocation: (location: TLocation) => void,
  hasManageAccountPrivilege: boolean
): TableColumnsType<TLocation> => [
  {
    title: "Location Name",
    dataIndex: "locationName",
    key: "locationName",
    sorter: (s1, s2) => s1.locationName?.localeCompare(s2.locationName),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()} role="none">
        <Input
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            onClick={() => clearFilters?.({ closeDropdown: true, confirm: true })}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm({ closeDropdown: true })}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
        </Space>
      </div>
    ),
    filterDropdownProps: {
      placement: "bottom",
    },
    filterSearch: true,
    onFilter: (value, location) => location.locationName.toLowerCase().includes((value as string).toLowerCase())
  },
  {
    title: "Short Name",
    dataIndex: "locationShortName",
    key: "locationShortName",
    width: "120px",
    sorter: (s1, s2) => s1.locationShortName?.localeCompare(s2.locationShortName)
  },
  {
    title: "Address",
    dataIndex: "address1",
    key: "address1",
    sorter: false
  },
  {
    title: "City",
    dataIndex: "city",
    key: "city",
    sorter: false
  },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
    sorter: false
  },
  {
    title: "Zip",
    dataIndex: "zip",
    key: "zip",
    sorter: false
  },
  {
    title: "Client Location Id",
    dataIndex: "extLocationId",
    key: "extLocationId",
    sorter: false
  },
  {
    title: "Primary",
    dataIndex: "locationId",
    width: "100px",
    render: (_, location) =>
      PrimaryLocatonCell(location, account, onChangePrimaryLocation, hasManageAccountPrivilege)
  },
  {
    title: "Edit",
    key: "edit",
    dataIndex: "locationId",
    render: (_, location) => (
      <EditIcon
        width="16px"
        height="16px"
        className="settings-edit-icon"
        onClick={() => onEditLocation(location)}
      />
    )
  }
];

const ManageLocations = () => {
  const { account, loading: picklistsLoading, refresh } = usePicklists();
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AxiosError | null>();
  const [locations, setLocations] = useState<TLocation[]>([]);
  const [editLocationState, setEditLocationState] = useState<TModalLocationState>({
    showModal: false
  });
  const [tableKey, setTableKey] = useState(1);
  const { webDUser } = useTDAuthentication();
  const hasManageAccountPrivilege: boolean =
    webDUser?.hasPrivilege(WebDPrivilege.ManageAccount) ?? false;
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      getAllLocations(accountId)
        .then(data => setLocations(data))
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [accountId]);

  const changePrimaryLocation = useCallback(
    (location: TLocation) => {
      if (accountId && location) {
        setLoading(true);
        setError(null);
        changePrimaryLocationForAccount(accountId, location.locationId)
          .then(() => refresh?.("Account"))
          .catch(setError)
          .finally(() => setLoading(false));
      }
    },
    [accountId, location, refresh]
  );

  const editLocation = useCallback(
    (location: TLocation) => {
      setEditLocationState({ showModal: true, location: location });
    },
    [accountId, location, refresh]
  );

  const onEditLocationClose = (newOrUpdatedLocation?: TLocation) => {
    setEditLocationState({ showModal: false });
    if (newOrUpdatedLocation) {
      setLocations(locations => {
        const index = locations.findIndex(
          l => l.locationId === newOrUpdatedLocation.locationId
        );
        if (index === -1) {
          locations.push(newOrUpdatedLocation);
        } else {
          locations.splice(index, 1, newOrUpdatedLocation);
        }
        return [...locations];
      });
      refresh?.("Locations");
    }
  };

  return (
    <Card
      style={{
        padding: "16px"
      }}
      className="default-border"
      styles={{ body: { padding: "0" } }}>
      <Flex wrap gap="middle" align="start" justify="space-between" style={{ marginBottom: "16px" }}>
        <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
          Manage Locations
        </Typography.Title>
        <Space size="small" direction="vertical" style={{ margin: 0 }}>
          <Button
            type="primary"
            size="middle"
            className="default-button primary-btn"
            icon={<AddLocation className="default-icon" />}
            iconPosition={"start"}
            title="Add New Location"
            onClick={() => setEditLocationState({ showModal: true })}>
            <span>Add New</span>
          </Button>
          <span style={{ fontSize: ".75rem" }}>{`Total Locations: ${locations.length}`}</span>
        </Space>
      </Flex>

      {(loading || picklistsLoading) && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      {!loading && (
        <Card className="card-style" styles={{ body: { padding: 0 } }}>
          <Table<TLocation>
            key={tableKey}
            columns={composeColumns(
              account!,
              changePrimaryLocation,
              editLocation,
              hasManageAccountPrivilege
            )}
            dataSource={locations.sort((a, b) => a.locationName.localeCompare(b.locationName))}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: {
                showSearch: false
              },
              pageSizeOptions: [5, 10, 20, 50, 100],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              }
            }}
            rowKey={"locationId"}
            className="table-style"
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      borderBottomWidth: "1px",
                      borderBottomColor: "#f0f0f0",
                      borderBottomStyle: "solid",
                      fontWeight: "500",
                    }}
                  />
                )
              }
            }}
            style={{ border: "none" }}
          />
        </Card>
      )}
      {editLocationState.showModal && (
        <AddOrUpdateLocation
          onClose={onEditLocationClose}
          location={editLocationState.location}
        />
      )}
    </Card>
  );
};

export default ManageLocations;
